// src/redux/reducers/errorLog.reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { deleteFirmware, fetchAllFirmware, fetchAvailableDevFirmware, fetchAvailableAdminFirmware, fetchAvailableFirmware, updateFirmware, uploadFirmware, uploadFirmwareWithProgress } from '../actions/firmware.actions';
import ListState from '../states/list.state';

const initialState: ListState = {
  loading: false,
  data: [],
  error: null,
  meta: null,
};

const FirmwareSlice = createSlice({
  name: 'firmware',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllFirmware.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllFirmware.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchAllFirmware.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
      })

      .addCase(fetchAvailableFirmware.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableFirmware.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchAvailableFirmware.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
      })

      .addCase(fetchAvailableDevFirmware.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableDevFirmware.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchAvailableDevFirmware.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
      })

      .addCase(fetchAvailableAdminFirmware.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableAdminFirmware.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchAvailableAdminFirmware.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
      })

      .addCase(deleteFirmware.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFirmware.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = [...state.data].filter((firmware:any)=>{return firmware?.id!==action.payload});
      })
      .addCase(deleteFirmware.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
      })
      .addCase(updateFirmware.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFirmware.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        
        if (action.payload?.is_latest) {
          // Check if the firmware already exists in the state
          const existingFirmwareIndex = state.data.findIndex((firmware: any) => firmware?.id === action.payload?.id);
          
          if (existingFirmwareIndex !== -1) {
            // Update existing firmware
            state.data[existingFirmwareIndex] = action.payload;
          } else {
            // Add new firmware to the beginning of the array
            state.data = [action.payload, ...state.data];
          }
          
          // Set is_latest to false for all other firmware of the same type
          state.data = state.data.map((firmware: any) => {
            if (firmware?.type === action.payload?.type && firmware?.id !== action.payload?.id) {
              return { ...firmware, is_latest: false };
            }
            return firmware;
          });
        } else {
          // Just add it to the beginning of the array without affecting other firmware
          const existingFirmwareIndex = state.data.findIndex((firmware: any) => firmware?.id === action.payload?.id);
          
          if (existingFirmwareIndex !== -1) {
            // Update existing firmware
            state.data[existingFirmwareIndex] = action.payload;
          } else {
            // Add new firmware to the beginning of the array
            state.data = [action.payload, ...state.data];
          }
        }
      })
      .addCase(updateFirmware.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
      })

      .addCase(uploadFirmware.pending, (state) => {
        state.error = null;
      })
      .addCase(uploadFirmware.fulfilled, (state, action: PayloadAction<any>) => {
        state.data = [...state.data].map((firmware:any)=>firmware?.id===action.payload?.id ? action.payload : firmware?.is_latest && firmware?.type===action.payload?.type ? {...firmware, is_latest:false} : firmware);
      })
      .addCase(uploadFirmware.rejected, (state, action) => {
        state.error = action.payload || 'An error occurred';
      })
      .addCase(uploadFirmwareWithProgress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadFirmwareWithProgress.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        
        if (action.payload?.is_latest) {
          // Check if the firmware already exists in the state
          const existingFirmwareIndex = state.data.findIndex((firmware: any) => firmware?.id === action.payload?.id);
          
          if (existingFirmwareIndex !== -1) {
            // Update existing firmware
            state.data[existingFirmwareIndex] = action.payload;
          } else {
            // Add new firmware to the beginning of the array
            state.data = [action.payload, ...state.data];
          }
          
          // Set is_latest to false for all other firmware of the same type
          state.data = state.data.map((firmware: any) => {
            if (firmware?.type === action.payload?.type && firmware?.id !== action.payload?.id) {
              return { ...firmware, is_latest: false };
            }
            return firmware;
          });
        } else {
          // Just add it to the beginning of the array without affecting other firmware
          const existingFirmwareIndex = state.data.findIndex((firmware: any) => firmware?.id === action.payload?.id);
          
          if (existingFirmwareIndex !== -1) {
            // Update existing firmware
            state.data[existingFirmwareIndex] = action.payload;
          } else {
            // Add new firmware to the beginning of the array
            state.data = [action.payload, ...state.data];
          }
        }
      })
      .addCase(uploadFirmwareWithProgress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
      });
  },
});

export const { clearError } = FirmwareSlice.actions;
export default FirmwareSlice.reducer;